import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { HlxLogo } from '~/components/logos';
import { paths } from '~/routing';
import { buildBrandSwitchUrl } from '~/routing/brandSwitch';

/**
 * Role-aware cross-brand entry point for the Vault sidebar.
 *
 * This must remain a native anchor because the destination is on the HLX
 * origin. The shared brand-switch builder preserves the deployment prefix and
 * adds Clerk's satellite-sync marker. VaultLayout supplies job opportunities
 * for providers, avoiding the provider-ineligible dashboard that caused the
 * MCV-1598 redirect loop. Authenticated navigation hides this entry while
 * impersonating a provider.
 *
 * [MCV-1945] The Provider HLX tour video button used to sit on this row; it now lives in the HLX
 * header (MarketHeader), next to the help menu, as the MCV tour does in the MCV header.
 */
export const VaultHlxSwitchLink = ({
  compact = false,
  switchTargetPath,
}: {
  compact?: boolean;
  switchTargetPath?: string;
}) => {
  const href = buildBrandSwitchUrl('hlx', switchTargetPath);
  const accessibleLabel =
    switchTargetPath === paths.hlx.jobOpportunities
      ? 'Go to HealthcareLX job opportunities'
      : 'Go to HealthcareLX';

  return (
    <a
      href={href}
      aria-label={accessibleLabel}
      title={compact ? accessibleLabel : undefined}
      data-cy={compact ? 'vault-hlx-switch-link-compact' : 'vault-hlx-switch-link'}
      className={classNames(
        'flex items-center rounded-md text-grey-200 hover:bg-white/50 transition-colors duration-150',
        compact ? 'w-9 h-9 justify-center shrink-0 mb-2' : 'gap-2.5 px-3 py-2 mx-2'
      )}
    >
      <span aria-hidden="true" className="flex shrink-0 items-center justify-center">
        <HlxLogo label="icon" height={compact ? 18 : 20} />
      </span>
      {!compact && (
        <>
          <span className="text-sm font-medium">HealthcareLX</span>
          <ArrowTopRightOnSquareIcon
            aria-hidden="true"
            className="ml-auto size-4 shrink-0 text-grey-400"
          />
        </>
      )}
    </a>
  );
};
